import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { Clipboard, Calendar, Activity, Heart } from "lucide-react";

export default function CarePlanning() {
  const planningSteps = [
    {
      title: "Initial Assessment",
      description: "Comprehensive evaluation of cognitive health and needs",
      icon: Clipboard,
      actions: [
        "Review medical history",
        "Conduct cognitive assessment",
        "Evaluate daily living activities",
        "Assess support system",
      ],
    },
    {
      title: "Plan Development",
      description: "Creating personalized care strategies",
      icon: Calendar,
      actions: [
        "Set care objectives",
        "Define intervention strategies",
        "Schedule activities",
        "Establish monitoring protocols",
      ],
    },
    {
      title: "Implementation",
      description: "Putting the care plan into action",
      icon: Activity,
      actions: [
        "Train care team members",
        "Set up monitoring tools",
        "Begin interventions",
        "Track initial progress",
      ],
    },
    {
      title: "Monitoring & Adjustment",
      description: "Ongoing evaluation and plan optimization",
      icon: Heart,
      actions: [
        "Regular progress reviews",
        "Adjust interventions",
        "Update care goals",
        "Collect feedback",
      ],
    },
  ];

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold">Care Planning</h1>
          <p className="text-xl text-gray-600">
            Develop and implement effective care plans using Scienza Health's comprehensive planning tools.
          </p>
        </div>

        <div className="space-y-6">
          {planningSteps.map((step) => (
            <Card key={step.title} className="p-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <step.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  {step.actions.map((action) => (
                    <div key={action} className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-primary rounded-full" />
                      <span className="text-sm">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        <section className="bg-muted p-6 rounded-lg">
          <h2 className="text-2xl font-semibold mb-4">Best Practices</h2>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full" />
              <span>Regularly review and update care plans</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full" />
              <span>Involve all stakeholders in the planning process</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full" />
              <span>Document all changes and interventions</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-primary rounded-full" />
              <span>Maintain clear communication channels</span>
            </li>
          </ul>
        </section>
      </div>
    </PageContainer>
  );
}